import {ColorKey} from "../../../utilis/types/Types";
import {unwrapData} from "../../apiResponse.ts";
import {api} from "../../api.ts";
export type CallState = keyof typeof CallStateEnum
export type CallKind = 'EXTERNAL' | 'INTERNAL' | 'CONFERENCE' | 'QUEUED';
export type GroupCallOutcome = 'OUTGOING' | 'OUTGOING_UNANSWERED' | 'JOINED' | 'DECLINED' | 'MISSED';
export interface CallParticipant {
    userId: number;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
    joinedAt?: string | null;
    declinedAt?: string | null;
}
export interface Call {
    callId: number;
    primaryContact: {
        contactId: number;
        firstName: string;
        lastName: string;
        iconColor: ColorKey;
        availability: string;
        ownerEmail?: string;
        contactEmail?: string;
        contactType?: { source: string };
        url?: string;
    };
    duplicatedContacts: {
        contactId: number;
        firstName: string;
        lastName: string;
        availability: string;
    }[];
    type: CallState;
    clientMsisdn: number;
    internalCallee: any
    exposedMsisdn: {
        msisdn: number;
        label: string;
        type: string;
        icon: string;
        iconColor: string;
    };
    startedAt: string;
    bridgetAt: string;
    finishedAt: string;
    user?: {
        userId: number;
        firstName: string;
        lastName: string;
        avatarUrl: string;
        contactType: any;
        url: any;
    },
    forwardedTo: number;
    forwardedAt: string;
    recordingId: number;
    spamCaller: boolean;
    wasDuringDnd?: boolean;
    hasVoicemail?: boolean;
    platform?: string;
    callType?: CallKind | null;
    roomId?: string | null;
    participants?: CallParticipant[] | null;
    myOutcome?: GroupCallOutcome | null;
    callIntent?: "audio" | "video" | null;
}

interface CallList {
    calls: Call[];
    totalCount: number;
}

export enum CallStateEnum {
    INBOUND = 'INBOUND',
    OUTBOUND = 'OUTBOUND',
}

export interface Transcription {
    clientLabel: any;
    segmentId: number;
    startTime: any;
    endTime: any;
    text: string;
    callee? : any;
}
export interface MatrixCallsHistory {
    roomId: string,
    callId: number,
    callState: string,
    startedAt: string;
    participantIds: number[];
    matrixCallId?: string;
    alreadyHangedUpUsers: number[]

}

export interface ContactCallHistory {
    groupedCalls: { [key: string]: Call[] };
    loadedCount: number;
}

export const callsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        fetchHistoryList: builder.query<CallList, any>({
            query: ({ from, to, contacts, users, groups, phoneNumbers, limit, userId, phrase }) => ({
                url: `/api/v1/calls`,
                params: { userId, from, to, contacts, users, groups, phoneNumbers, limit, phrase },
            }),
            transformResponse: (response: { data: Call[] }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    calls: unwrapData(response) || [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : 0,
                };
            },
            providesTags: (_, __, arg) => [
                { type: 'Contact',id: arg.userId },
                {type: 'Numbers'},
                {type: 'Calls', id: 'HISTORY'},
            ],

        }),
        fetchContactHistory: builder.query<ContactCallHistory, { contactId: number, phrase?: string, userId?: string, limit?: number, users?: number[], phoneNumbers?: number[], from?: string, to?: string }>({
            query: ({contactId, phrase, limit, users, phoneNumbers, from, to}) => ({
                url:`/api/v1/contacts/${contactId}/calls`,
                params: { phrase, limit, users, phoneNumbers, from, to }
            }),
            transformResponse: (response: { data: Call[] }) => {
                const calls = unwrapData(response) || [];
                const groupedCalls = calls.reduce((acc: { [key: string]: Call[] }, call) => {
                    const date = call.startedAt?.split('T')[0] || 'unknown';
                    if (!acc[date]) {
                        acc[date] = [];
                    }
                    acc[date].push(call);
                    return acc;
                }, {});
                return {
                    groupedCalls,
                    loadedCount: calls.length,
                };
            },
        }),
        fetchCall: builder.query<Call, number>({
            query: (callId) => `/api/v1/calls/${callId}`,
            transformResponse: (response: { data: Call }) => unwrapData(response),
        }),
        fetchCallRecord: builder.query<string, number>({
            query: (recordingId) => ({
                url: `/api/v1/recordings/${recordingId}`,
                responseHandler: (response: any) => response.blob(),
            }),
            transformResponse: (data: Blob) => {
                const audioBlob = new Blob([data], { type: 'audio/mpeg' });
                return URL.createObjectURL(audioBlob);
            },
        }),
        fetchOngoingCall: builder.query<Call[], any>({
            query: ({ from, to, contacts, users, groups, phoneNumbers, limit, userId }) => ({
                url: `/api/v1/calls`,
                params: { userId, from, to, contacts, users, groups, phoneNumbers, limit, ongoing: true },
            }),
            transformResponse: (response: { data: Call[] }) => unwrapData(response),
            providesTags: [{type: 'Calls', id: 'ONGOING'}],
        }),

        fetchInboundQueueCall: builder.query<Call[], string | null>({
            query: (userId) => ({
                url: `/api/v1/calls`,
                params: { userId, callState: 'STARTED' },
            }),
            transformResponse: (response: { data: Call[] }) =>
                unwrapData(response).filter((call) => call.type === CallStateEnum.INBOUND),
        }),
        fetchCallTranscript: builder.query<any, number>({
            query: (callId) => `/api/v1/calls/${callId}/transcripts`,
            providesTags: (_result, _error, callId) => [
                { type: 'Call', id: 'LIST' },
                { type: 'CallTranscript', id: callId }
            ],
        }),
        requestCallTranscription: builder.mutation<any, {callId: number }>({
            query: ({ callId}) => ({
                url: `/api/v1/calls/${callId}/transcripts`,
                method: 'POST',
                body:{  },
            }),
            invalidatesTags: (_result, _error, { callId }) => [
                { type: 'CallTranscript', id: callId },
                { type: 'CallSummary', id: callId }
            ],
        }),
        requestCallSummary: builder.mutation<any, {callId: number }>({
            query: ({ callId}) => ({
                url: `/api/v1/calls/${callId}/summaries`,
                method: 'POST',
                body:{  },
            }),
            invalidatesTags: (_result, _error, { callId }) => [
                { type: 'CallTranscript', id: callId },
                { type: 'CallSummary', id: callId }
            ],
        }),
        fetchCallSummary: builder.query<any, number>({
            query: (callId) => `/api/v1/calls/${callId}/summaries`,
            providesTags: (_result, _error, callId) => [
                { type: 'Call', id: 'LIST' },
                { type: 'CallSummary', id: callId }
            ],
        }),
        fetchCallsHistory: builder.query<{data: MatrixCallsHistory[] }, { userId: string }>({
            query: ({userId}) => ({
                url:`/api/v1/call-history/chat-state/?userId=${userId}&callState=STARTED,TALKING`,
            }),
        }),
    }),
});

export const {
    useFetchHistoryListQuery,
    useFetchContactHistoryQuery,
    useFetchCallQuery,
    useFetchCallRecordQuery,
    useFetchOngoingCallQuery,
    useFetchInboundQueueCallQuery,
    useLazyFetchCallRecordQuery,
    useFetchCallTranscriptQuery,
    useRequestCallTranscriptionMutation,
    useRequestCallSummaryMutation,
    useFetchCallSummaryQuery,
    useFetchCallsHistoryQuery

} = callsApi;
